import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Switch } from '../../../components/ui/switch';
import { toast } from 'sonner';
import { Headphones, Download, FileText, Loader2, CheckCircle2, TrendingUp, MessageSquareWarning, Ban, IndianRupee } from 'lucide-react';
import { isMgr, fmtDate, STATUS_META, OUTCOME_META } from './demoConstants';
import DemoPeriodSelector from './DemoPeriodSelector';
import SearchSelect from './SearchSelect';
import DemoDetailModal from './DemoDetailModal';
import DemoDrilldownModal from './DemoDrilldownModal';
import { PageHeader, Stat, EmptyState } from './DemoUI';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const csv = (rows) => {
  const head = ['Counsellor', 'Demos', 'Pending Approval', 'Approved', 'Admissions', 'No Admission', 'Follow-up',
    'Outcome Pending', 'Void', 'Conversion %', 'Admission Value excl GST', 'Admission Value incl GST', 'Trainer FB Missing', 'Counsellor FB Missing', 'FB Compliance %'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const body = rows.map((r) => [r.name, r.total, r.pending_approval, r.approved, r.admissions, r.no_admission,
    r.follow_up, r.outcome_pending, r.void, r.conversion_pct, r.admission_value, r.admission_value_gross, r.trainer_fb_missing,
    r.counsellor_fb_missing, r.feedback_compliance_pct].map(esc).join(','));
  return [head.map(esc).join(','), ...body].join('\n');
};

export default function DemoCounsellorLoadPage({ embedded = false }) {
  const { user } = useAuth();
  const role = user?.role;
  const mgr = isMgr(role);
  const [period, setPeriod] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [masters, setMasters] = useState({ counsellors: [], courses: [], trainers: [] });
  const [f, setF] = useState({ counsellor_id: '', trainer_id: '', course_id: '' });
  const [includeVoid, setIncludeVoid] = useState(false);
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    api.get('/training/demos/masters/all')
      .then((r) => setMasters({ trainers: r.data.trainers || [], counsellors: r.data.counsellors || [], courses: r.data.courses || [] }))
      .catch(() => {});
  }, []);

  const params = useMemo(() => {
    const p = { date_from: period?.from_date, date_to: period?.to_date };
    Object.entries(f).forEach(([k, v]) => { if (v) p[k] = v; });
    if (includeVoid) p.include_void = 'true';
    return p;
  }, [period, f, includeVoid]);

  const load = useCallback(async () => {
    if (!period) return;
    setLoading(true);
    try {
      const r = await api.get('/training/demos/reports/counsellor-load', { params });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load the counsellor report'); }
    finally { setLoading(false); }
  }, [period, params]);
  useEffect(() => { load(); }, [load]);

  const downloadCsv = () => {
    const blob = new Blob([csv(data?.by_counsellor || [])], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `counsellor-demo-load-${period?.from_date}_${period?.to_date}.csv`;
    a.click();
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await api.get('/training/demos/reports/counsellor-load.pdf', { params, responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      a.download = `counsellor-demo-load-${period?.from_date}_${period?.to_date}.pdf`;
      a.click();
    } catch { toast.error('PDF export failed'); }
    finally { setPdfBusy(false); }
  };

  const t = data?.totals || {};
  const all = data?.rows || [];
  const openDrill = (title, subtitle, list) => setDrill({ title, subtitle, rows: list });
  const isMissing = (r) => !r.void && r.status === 'approved' && (r.trainer_feedback_missing || r.counsellor_feedback_missing);
  const kpiDrills = {
    demos: () => openDrill('All demos', 'Every demo in this period', all),
    approved: () => openDrill('Approved demos', 'Demos approved by the Training Manager', all.filter((r) => r.status === 'approved' && !r.void)),
    admissions: () => openDrill('Admissions', 'Demos that converted into an admission', all.filter((r) => r.outcome_result === 'admission' && !r.void)),
    incomplete: () => openDrill('Feedback pending', 'Approved demos still missing a trainer or counsellor feedback', all.filter(isMissing)),
  };
  const rows = onlyIncomplete
    ? all.filter((r) => !r.void && r.status === 'approved' && (r.trainer_feedback_missing || r.counsellor_feedback_missing))
    : all;

  return (
    <div className={embedded ? "space-y-6" : "space-y-8"} data-testid="counsellor-load-page">
      <PageHeader embedded={embedded} icon={Headphones} eyebrow="Trainer Demo" title="Counsellor Demo Load"
        subtitle="How many demos each counsellor ran, what converted, and whose feedback is still missing."
        testid="counsellor-load-header">
        <Button variant="outline" size="sm" className="h-10" onClick={downloadCsv} disabled={!(data?.by_counsellor || []).length} data-testid="load-export-csv">
          <Download className="w-4 h-4 mr-1.5" />CSV
        </Button>
        <Button size="sm" className="h-10 bg-violet-600 hover:bg-violet-700 text-white" onClick={downloadPdf} disabled={pdfBusy || !(data?.by_counsellor || []).length} data-testid="load-export-pdf">
          {pdfBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileText className="w-4 h-4 mr-1.5" />}PDF for sign-off
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="load-kpi-rail">
        <Stat icon={Headphones} label="Total Demos" value={t.demos ?? 0} tone="violet" sub={`${t.counsellors ?? 0} counsellor(s)`} onClick={kpiDrills.demos} testid="lkpi-demos" />
        <Stat icon={CheckCircle2} label="Approved" value={t.approved ?? 0} tone="sky" onClick={kpiDrills.approved} testid="lkpi-approved" />
        <Stat icon={CheckCircle2} label="Admissions" value={t.admissions ?? 0} tone="emerald" onClick={kpiDrills.admissions} testid="lkpi-admissions" />
        <Stat icon={TrendingUp} label="Conversion" value={`${t.conversion_pct ?? 0}%`} tone="violet" sub="of approved demos" testid="lkpi-conversion" />
        <Stat icon={IndianRupee} label="Adm. Value excl. GST" value={inr(t.admission_value)} tone="emerald" sub={`incl. GST ${inr(t.admission_value_gross)}`} testid="lkpi-value" />
        <Stat icon={MessageSquareWarning} label="Feedback Pending" value={t.feedback_incomplete ?? 0} tone="amber" sub="trainer or counsellor" onClick={kpiDrills.incomplete} testid="lkpi-incomplete" />
      </div>

      <Card className="p-4 border flex flex-wrap items-center gap-2" data-testid="load-filter-bar">
        <DemoPeriodSelector onChange={setPeriod} defaultKey="this_month" />
        <SearchSelect value={f.counsellor_id} onChange={(v) => setF((p) => ({ ...p, counsellor_id: v }))} options={masters.counsellors} allowAll allLabel="All counsellors" testid="load-filter-counsellor" className="w-44" />
        <SearchSelect value={f.trainer_id} onChange={(v) => setF((p) => ({ ...p, trainer_id: v }))} options={masters.trainers} allowAll allLabel="All trainers" testid="load-filter-trainer" className="w-44" />
        <SearchSelect value={f.course_id} onChange={(v) => setF((p) => ({ ...p, course_id: v }))} options={masters.courses} allowAll allLabel="All courses" testid="load-filter-course" className="w-48" />
        {mgr && (
          <label className="flex items-center gap-2 text-[11.5px] text-slate-600 pl-1">
            <Switch checked={includeVoid} onCheckedChange={setIncludeVoid} data-testid="load-include-void" />Include void
          </label>
        )}
        <Button variant="ghost" size="sm" className="h-9 text-[12px] text-slate-500" onClick={() => { setF({ counsellor_id: '', trainer_id: '', course_id: '' }); setIncludeVoid(false); setOnlyIncomplete(false); }} data-testid="load-clear-filters">Clear</Button>
      </Card>

      <section className="space-y-3" data-testid="load-summary-section">
        <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wider">Counsellor summary</h2>
        <Card className="border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 font-medium border-b border-slate-200">
                <th className="px-4 py-3">Counsellor</th><th className="px-4 py-3 text-right">Demos</th>
                <th className="px-4 py-3 text-right">Pend. Appr</th><th className="px-4 py-3 text-right">Approved</th>
                <th className="px-4 py-3 text-right">Admissions</th><th className="px-4 py-3 text-right">No Adm.</th>
                <th className="px-4 py-3 text-right">Follow-up</th><th className="px-4 py-3 text-right">Outcome Pend.</th>
                <th className="px-4 py-3 text-right">Void</th><th className="px-4 py-3 text-right">Conv %</th>
                <th className="px-4 py-3 text-right">Adm. Value excl. GST</th><th className="px-4 py-3 text-right">FB Compliance</th>
              </tr></thead>
              <tbody>
                {(data?.by_counsellor || []).map((c) => (
                  <tr key={c.name} onClick={() => openDrill(c.name, `${c.total} demo(s) · ${c.admissions} admission(s) · ${c.conversion_pct}% conversion in this period`, all.filter((r) => r.counsellor_name === c.name))}
                    className={`border-b border-slate-100 last:border-0 hover:bg-violet-50 cursor-pointer transition-colors ${c.feedback_compliance_pct < 100 ? 'bg-amber-50/50' : ''}`} data-testid={`load-row-${c.id || c.name}`}>
                    <td className="px-4 py-3 font-semibold text-slate-800">{c.name}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-900 font-semibold">{c.total}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-violet-700">{c.pending_approval || '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-600">{c.approved}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-semibold">{c.admissions}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-rose-700">{c.no_admission || '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-sky-700">{c.follow_up || '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-amber-700">{c.outcome_pending || '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-400">{c.void || '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-violet-700">{c.conversion_pct}%</td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">{inr(c.admission_value)}</td>
                    <td className={`px-4 py-3 text-right tabular-nums font-semibold ${c.feedback_compliance_pct < 100 ? 'text-amber-700' : 'text-emerald-700'}`}>{c.feedback_compliance_pct}%</td>
                  </tr>
                ))}
                {(data?.by_counsellor || []).length === 0 && <tr><td colSpan={12} className="px-4 py-6 text-center text-[12px] text-slate-400">No demos in this period.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </section>

      <section className="space-y-3" data-testid="load-detail-section">
        <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wider">
          {onlyIncomplete ? 'Demos with feedback pending' : 'All demos'} <span className="text-slate-400 normal-case">({rows.length})</span>
        </h2>
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading demos…</div>
        ) : rows.length === 0 ? (
          <Card className="border"><EmptyState title={onlyIncomplete ? 'Every approved demo has both feedbacks' : 'No demos in this period'} hint="Change the period or clear the filters." /></Card>
        ) : (
          <Card className="border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 font-medium border-b border-slate-200">
                  <th className="px-4 py-3">Demo</th><th className="px-4 py-3">Student</th><th className="px-4 py-3">Course</th>
                  <th className="px-4 py-3">Trainer</th><th className="px-4 py-3">Counsellor</th><th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Outcome</th><th className="px-4 py-3">Feedback</th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => {
                    const missing = r.status === 'approved' && !r.void && (r.trainer_feedback_missing || r.counsellor_feedback_missing);
                    return (
                      <tr key={r.id} className={`border-b border-slate-100 last:border-0 hover:bg-violet-50 transition-colors ${r.void ? 'opacity-60' : missing ? 'bg-amber-50/60' : ''}`} data-testid={`load-demo-row-${r.id}`}>
                        <td className="px-4 py-3">
                          <button onClick={() => setOpenId(r.id)} className={`font-mono text-[11.5px] text-violet-700 hover:underline ${r.void ? 'line-through' : ''}`} data-testid={`load-demo-link-${r.id}`}>{r.demo_code}</button>
                          <span className="block text-[10.5px] text-slate-400">{fmtDate(r.demo_date)}</span>
                        </td>
                        <td className="px-4 py-3 text-slate-800">{r.student_name || '—'}</td>
                        <td className="px-4 py-3 text-slate-700">{r.course_name}</td>
                        <td className="px-4 py-3 text-slate-700">{r.trainer_name}</td>
                        <td className="px-4 py-3 text-slate-700">{r.counsellor_name}</td>
                        <td className="px-4 py-3">
                          {r.void
                            ? <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold bg-slate-200 text-slate-600 border border-slate-300" title={r.void_reason} data-testid={`void-badge-${r.id}`}><Ban className="w-2.5 h-2.5" />VOID</span>
                            : <span className={STATUS_META[r.status]?.cls}>{STATUS_META[r.status]?.label || r.status}</span>}
                        </td>
                        <td className="px-4 py-3">{r.outcome_result ? <span className={OUTCOME_META[r.outcome_result]?.cls}>{OUTCOME_META[r.outcome_result]?.label}</span> : <span className="text-slate-300">—</span>}</td>
                        <td className="px-4 py-3">
                          <span className="flex flex-wrap gap-1">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${r.trainer_feedback_missing ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>Trainer {r.trainer_feedback_missing ? '⏳' : '✓'}</span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${r.counsellor_feedback_missing ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>Counsellor {r.counsellor_feedback_missing ? '⏳' : '✓'}</span>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      <DemoDrilldownModal open={!!drill} onClose={() => setDrill(null)} title={drill?.title} subtitle={drill?.subtitle}
        rows={drill?.rows || []} variant="demos" onOpenDemo={(id) => { setDrill(null); setOpenId(id); }} />
      <DemoDetailModal demoId={openId} readOnly={!mgr} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}
